import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Spinner, Badge, StatCard, Input, Select } from "@/components/ui";
import { money } from "@/lib/utils";
import { Receipt, DollarSign, TrendingDown, CheckCircle2, Search } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const STATUS_COLOR = { Paid: "#4f46e5", Pending: "#94a3b8", Denied: "#dc2626", Adjudicated: "#3b82f6" };

export default function Billing() {
  const [summary, setSummary] = useState(null);
  const [claims, setClaims] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/claims-summary").then((r) => setSummary(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/claims", { params: { status, search } }).then((r) => setClaims(r.data)), 250);
    return () => clearTimeout(t);
  }, [status, search]);

  if (!summary) return <Spinner />;

  return (
    <div className="vc-fade-up">
      <PageHeader title="Billing & Claims" subtitle="End-to-end 837 claims intake, adjudication, and 835 payment posting. All claim details on one page." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Claims" value={summary.total_claims} sub="837P / 837I" icon={Receipt} tone="brand" />
        <StatCard label="Total Billed" value={money(summary.total_billed)} icon={DollarSign} tone="blue" />
        <StatCard label="Total Paid" value={money(summary.total_paid)} sub="835 posted" icon={CheckCircle2} tone="brand" />
        <StatCard label="Denial Rate" value={`${summary.denial_rate}%`} sub="Of total claims" icon={TrendingDown} tone="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-6">
          <h3 className="font-display font-semibold text-lg mb-4">Claims by Status</h3>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={summary.by_status} dataKey="count" nameKey="status" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {summary.by_status.map((e) => <Cell key={e.status} fill={STATUS_COLOR[e.status] || "#94a3b8"} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-0 lg:col-span-2 overflow-hidden">
          <div className="p-4 flex flex-col sm:flex-row gap-3 border-b border-border">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search claim # or member…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="claim-search" />
            </div>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="claim-status-filter">
              <option value="">All</option><option>Paid</option><option>Pending</option><option>Denied</option><option>Adjudicated</option>
            </Select>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            {claims === null ? <Spinner /> : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card"><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Claim #</th><th className="px-4 py-3 font-semibold">Member</th>
                  <th className="px-4 py-3 font-semibold">HCPCS</th><th className="px-4 py-3 font-semibold text-right">Billed</th>
                  <th className="px-4 py-3 font-semibold text-right">Paid</th><th className="px-4 py-3 font-semibold">Status</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {claims.map((c) => (
                    <tr key={c.id} className="hover:bg-muted transition-colors">
                      <td className="px-4 py-2.5 font-mono text-xs">{c.claim_number}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{c.member_name}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{c.hcpcs}{c.modifiers?.length ? " " + c.modifiers.join(",") : ""}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{money(c.billed_amount)}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{money(c.paid_amount)}</td>
                      <td className="px-4 py-2.5"><Badge tone={c.status}>{c.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
